import * as React from 'react';

import styled from 'styled-components';

import { COLOR, FONT_SIZE, SPACING } from '../../packages/constants';
import { HelperModal, QuestionMark } from '../helper-modal';
import { NumberInput, NumberInputProps } from '../number-input';

export interface NumberFieldProps extends Omit<NumberInputProps, 'ariaLabel'> {
    hasHelper?: boolean;
    helperContent?: React.ReactNode;
    label: string;
    validationMessage?: string;
    thinLabel?: boolean;
}

interface StyledTextLabelProps {
    disabled?: boolean;
    invalid?: boolean;
    thinLabel?: boolean;
}

const wrapperStyles = `
    display: flex;
    flex-direction: column;
`;

const Wrapper = styled.label`
    ${wrapperStyles}
`;

const WrapperHasHelper = styled.div`
    ${wrapperStyles}
`;

const StyledLabelWrapper = styled.div`
    display: inline-flex;
`;

const StyledTextLabel = styled.span<StyledTextLabelProps>`
    display: block;
    padding-bottom: ${SPACING.XX_SMALL};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

const StyledLabelHasHelper = styled.label<StyledTextLabelProps>`
    display: inline-block;
    padding-bottom: ${SPACING.XX_SMALL};
    margin-right: ${SPACING.X_SMALL};
    font-size: ${FONT_SIZE.SMALL};
    line-height: ${FONT_SIZE.MEDIUM};
    font-weight: ${(props: StyledTextLabelProps): string => (props.thinLabel ? '400' : '700')};
    color: ${(props: StyledTextLabelProps): string => (props.invalid ? COLOR.PRIMARY.RED : COLOR.MAIN.LABEL_TEXT)};
    opacity: ${(props: StyledTextLabelProps): string => (props.disabled ? '0.7' : '1')};
`;

export const NumberField = React.forwardRef<HTMLInputElement, NumberFieldProps>(
    (
        {
            autoComplete,
            autoFocus,
            dataId,
            defaultValue,
            disabled,
            hasHelper,
            helperContent,
            id,
            invalid,
            label,
            max,
            min,
            name,
            placeholder,
            required = false,
            step,
            onChange,
            thinLabel,
            value,
            className,
            ...props
        },
        ref,
    ) => {
        return hasHelper ? (
            <WrapperHasHelper className={className} {...props}>
                <StyledLabelWrapper>
                    <StyledLabelHasHelper
                        data-test-id={`${dataId}-label`}
                        thinLabel={thinLabel}
                        invalid={invalid}
                        disabled={disabled}
                        htmlFor={id}
                    >
                        {label}
                    </StyledLabelHasHelper>
                    <HelperModal dataId={dataId} modalContent={helperContent}>
                        <QuestionMark />
                    </HelperModal>
                </StyledLabelWrapper>
                <NumberInput
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                    dataId={`${dataId}-input`}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    id={id}
                    invalid={invalid}
                    max={max}
                    min={min}
                    name={name}
                    onChange={onChange}
                    placeholder={placeholder}
                    required={required}
                    ref={ref}
                    step={step}
                    value={value}
                />
            </WrapperHasHelper>
        ) : (
            <Wrapper data-test-id={`${dataId}-label`} className={className} {...props}>
                <StyledTextLabel invalid={invalid} disabled={disabled} thinLabel={thinLabel}>
                    {label}
                </StyledTextLabel>
                <NumberInput
                    autoComplete={autoComplete}
                    autoFocus={autoFocus}
                    dataId={`${dataId}-input`}
                    defaultValue={defaultValue}
                    disabled={disabled}
                    id={id}
                    invalid={invalid}
                    max={max}
                    min={min}
                    name={name}
                    onChange={onChange}
                    placeholder={placeholder}
                    ref={ref}
                    required={required}
                    step={step}
                    value={value}
                />
            </Wrapper>
        );
    },
);
NumberField.displayName = 'NumberField';
